"use client";
import { useTranslations } from "@zapier/i18n";

import { useEffect, useState } from "react";
import { Avatar, Link, ModalDialogRoot, Text } from "@zapier/design-system";
import { buildStaticAssetUrl } from "@zapier/utils";
import { buildAccountSwitcherUrl } from "@zapier/identity";
import { FolderDiscovery } from "@zapier/client";

import styles from "./AccountMismatchModal.module.css";

interface Props {
  account: FolderDiscovery | null | undefined;
  /** Text to show to continue with mismatched account */
  continueText?: string;
  /** The object being operated on by the mismatched account */
  noun: "Zap" | "folder";
}

export const AccountMismatchModal = ({
  account,
  continueText,
  noun,
}: Props) => {
  const t = useTranslations("account-mismatch-modal");

  const [accountSwitchUrl, setAccountSwitchUrl] = useState("");

  // run this in useEffect so it only runs in the browser
  useEffect(() => {
    setAccountSwitchUrl(buildAccountSwitcherUrl(account?.id || ""));
  }, [account?.id]);

  const name = account?.name || t("fallbacks.account-name");

  return (
    <ModalDialogRoot
      isOpen={true}
      onOpenChange={() => {}}
      isDismissable={false}
    >
      <div className={styles.root}>
        <div className={styles.header}>
          <Avatar
            name={name}
            size="medium"
            url={buildStaticAssetUrl(account?.image || "")}
          />
          <Text tag="h1" type="Body4">
            {name}
          </Text>
        </div>
        <Text tag="p" type="Body1">
          {t("messages.account-switch-prompt", { noun, name })}
        </Text>
        <div className={styles.actions}>
          <Link variant="primary" href={accountSwitchUrl} target="_self">
            {t("buttons.switch-to-account", { name })}
          </Link>
          <Link variant="secondary" href="/app/assets/zaps/folders">
            {continueText || t("buttons.cancel")}
          </Link>
        </div>
      </div>
    </ModalDialogRoot>
  );
};
